/** Emoji Registry DTOs — wire types mirroring n450s_backend/modules/emoji (v2). */

export type EmojiOwnershipState = "unclaimed" | "signature" | "reserved";
export type EmojiStatus = "active" | "retired";
export type EmojiReservationKind = "card" | "page" | "sitebot" | "system" | "other";

export const EMOJI_RESERVATION_KINDS: EmojiReservationKind[] = [
  "card",
  "page",
  "sitebot",
  "system",
  "other",
];

export interface EmojiReservationInfo {
  kind: EmojiReservationKind;
  refId: string | null;
  label: string | null;
}

export interface PublicEmojiEntry {
  id: string;
  emoji: string;
  category: string;
  tags: string[];
  name: string | null;
  status: EmojiStatus;
  ownership: {
    state: EmojiOwnershipState;
    ownerUsername: string | null;
    claimedAt: string | null;
    expiresAt: string | null;
  };
  reservation: EmojiReservationInfo | null;
  usage: {
    totalAllTime: number;
    last30Days: number;
    last7Days: number;
  };
}

/**
 * Slim selector entry — the catalog is ~1,900 emojis, so the wire shape is
 * deliberately compact (the emoji itself is the identifier).
 */
export interface SelectorEmoji {
  /** the emoji (canonical display form) */
  e: string;
  /** official name (searchable) */
  n: string | null;
  /** owned by the requesting user as their signature */
  mine: boolean;
  /** signature-owned by someone else */
  taken: boolean;
  /** owner username when signature-claimed (tooltips) */
  owner: string | null;
  /** already burned by the user in a generated AOTD (per-user ledger) */
  used: boolean;
  /** staged for the user's next AOTD */
  staged: boolean;
  /** burned, so re-usable only via a Reclamation unlock */
  revivable: boolean;
  /** reserved by a platform entity (card / page / sitebot / system) */
  reserved: { kind: EmojiReservationKind; label: string | null } | null;
}

export interface SelectorState {
  me: {
    userId: string | null;
    signatureEmoji: string | null;
    hasSignature: boolean;
  };
  categories: Array<{ category: string; emojis: SelectorEmoji[] }>;
  total: number;
}

export interface AdminEmojiRow extends PublicEmojiEntry {
  source: string;
  createdAt: string;
  updatedAt: string;
}

export interface EmojiCandidate {
  emoji: string;
  count: number;
  contexts: string[];
  lastSeenAt: string;
}

export interface AdminEmojiStats {
  total: number;
  active: number;
  retired: number;
  unclaimed: number;
  signatures: number;
  reserved: number;
  reservedByKind: Record<string, number>;
  byCategory: Array<{ category: string; count: number }>;
  candidates: number;
}

export interface SyncReservationsReport {
  reserved: Array<{ emoji: string; kind: string; refId: string; label: string }>;
  alreadyReserved: number;
  conflicts: Array<{ emoji: string; refId: string; reason: string }>;
  notInRegistry: Array<{ emoji: string; refId: string }>;
}

/* ---- API response envelopes ---- */
export interface SelectorResponse {
  success: boolean;
  data: SelectorState;
}
export interface ClaimResponse {
  success: boolean;
  data: PublicEmojiEntry;
}
export interface AdminListResponse {
  success: boolean;
  total: number;
  limit: number;
  offset: number;
  entries: AdminEmojiRow[];
}
export interface AdminEntryResponse {
  success: boolean;
  data: AdminEmojiRow | PublicEmojiEntry;
}
export interface AdminStatsResponse {
  success: boolean;
  stats: AdminEmojiStats;
}
export interface CandidatesResponse {
  success: boolean;
  candidates: EmojiCandidate[];
}
export interface SyncReservationsResponse {
  success: boolean;
  report: SyncReservationsReport;
}

/* ---- request bodies ---- */
export interface CreateEmojiRequest {
  emoji: string;
  category: string;
  tags?: string[];
  name?: string | null;
}
export interface UpdateEmojiRequest {
  category?: string;
  tags?: string[];
  name?: string | null;
  status?: EmojiStatus;
}
export interface ReserveEmojiRequest {
  kind: EmojiReservationKind;
  refId?: string | null;
  label?: string | null;
  note?: string | null;
}
export interface AdminListFilter {
  status?: EmojiStatus;
  state?: EmojiOwnershipState;
  category?: string;
  search?: string;
  reservedKind?: EmojiReservationKind;
  limit?: number;
  offset?: number;
}
